import { useMutation } from '@tanstack/react-query';

import { Botao } from '@/componentes/Botao';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { comCorpo, explicarFalha } from '@/lib/pedido';
import { useSessao } from '@/modulos/sessao/consultas';
import { UtilidadeDaResposta } from '@/modulos/tecnico/UtilidadeDaResposta';

/**
 * IA 03, segunda metade — a revisão do fechamento, ANTES de liberar. Manda a
 * solução e a evidência que estão sendo escritas, e mostra os pontos que a IA
 * levantou, citando a própria O.S. e o manual. Não aprova nem barra: quem
 * libera é a pessoa. Some sozinho para quem não pode perguntar ao assistente.
 */

interface Revisao {
  /** O registro da revisão (0028): é por ele que a pessoa diz se serviu (0076). */
  readonly id?: string;
  readonly sabe: boolean;
  readonly resposta: string;
  readonly falta: string | null;
  readonly citacoes: readonly { readonly trechoId: string; readonly titulo: string; readonly pagina: number | null; readonly texto: string }[];
}

export function RevisarFechamento({
  ordemId,
  solucao,
  evidencia,
}: {
  readonly ordemId: string;
  readonly solucao: string;
  readonly evidencia: string;
}) {
  const sessao = useSessao();
  const pode = (sessao.data?.permissoes ?? []).includes('ia.perguntar');
  const revisar = useMutation({
    mutationFn: () =>
      comCorpo<Revisao>('POST', `/oficina/ordens/${encodeURIComponent(ordemId)}/revisao-do-fechamento`, {
        solucao: solucao.trim(),
        evidencia: evidencia.trim(),
      }),
    /* A revisão aparece logo abaixo do botão, e a recusa também: elas são o retorno. */
    meta: { silencioso: true, silenciarFalha: true },
  });
  if (!pode) return null;

  const pronto = solucao.trim() !== '' && evidencia.trim() !== '';
  const r = revisar.data;
  const recusa = revisar.isError ? explicarFalha(revisar.error) : null;

  return (
    <div className="flex flex-col gap-2 rounded-controle border border-borda-sutil p-3">
      <div className="flex flex-wrap items-center gap-3">
        <Botao
          nivel="consulta"
          type="button"
          disabled={!pronto || revisar.isPending}
          onClick={() => {
            revisar.mutate();
          }}
        >
          {revisar.isPending ? 'Revisando…' : 'Revisar fechamento'}
        </Botao>
        <span className="txt-apoio text-tinta-apagada">
          {pronto
            ? 'A IA confere a solução e a evidência contra a O.S. e o manual. Não libera nem barra. Tem custo.'
            : 'Escreva o que foi feito e como se verificou, e peça a revisão antes de liberar.'}
        </span>
      </div>
      {recusa ? (
        <p role="alert" className="txt-apoio text-tinta">{`${recusa.erro}. ${recusa.comoResolver}`}</p>
      ) : null}
      {/* Enquanto a IA confere (p.32), o lugar da revisão vira a forma dela: o
          parágrafo e as citações. A revisão anterior sai, para não ser lida
          como a nova. */}
      {revisar.isPending ? (
        <Carregando oQue="Conferindo o fechamento contra a O.S. e o manual" className="flex flex-col gap-3 py-1">
          <Barra className="h-3 w-full" />
          <Barra className="h-3 w-4/5" />
          <Barra className="ml-5 h-3 w-2/3" />
          <Barra className="ml-5 h-3 w-1/2" />
        </Carregando>
      ) : r ? (
        <div role="status" className="flex flex-col gap-2">
          <p className="txt-corpo text-tinta">{r.sabe ? r.resposta : `Sem revisão: ${r.falta ?? 'a resposta não citou a O.S. nem o manual'}.`}</p>
          {r.citacoes.length > 0 ? (
            <ol className="flex list-decimal flex-col gap-1 pl-5">
              {r.citacoes.map((c) => (
                <li key={c.trechoId} className="txt-apoio text-tinta-secundaria">
                  <span className="text-tinta">{c.pagina !== null ? `${c.titulo}, página ${String(c.pagina)}` : c.titulo}</span>
                  {` — “${c.texto.length > 180 ? `${c.texto.slice(0, 180)}…` : c.texto}”`}
                </li>
              ))}
            </ol>
          ) : null}
          {r.id ? <UtilidadeDaResposta key={r.id} perguntaId={r.id} /> : null}
        </div>
      ) : null}
    </div>
  );
}
